import api from "@/axiosInterceptor";
import { fetchMetaData, getCachedMetaData } from "@/utils/cache";

const PAGE_SIZE = 1000;
const PAGE_CONCURRENCY = 3;

function isOk(response) {
  const code = response && response.data && response.data.statusCode;
  return code === 200 || code === 201;
}

async function fetchSamplesPage(pageNumber, tenantId) {
  const response = await api.get("public/api/samples", {
    params: { pageSize: PAGE_SIZE, pageNumber },
    headers: tenantId ? { "X-TenantID": tenantId } : undefined,
  });
  if (!isOk(response)) {
    const message = (response && response.data && response.data.message) || "Unexpected samples response";
    throw new Error(message);
  }
  const data = response.data.data || {};
  return {
    samples: Array.isArray(data.samples) ? data.samples : [],
    totalRecords: data.pagination && Number(data.pagination.TotalRecords),
  };
}

export async function fetchAllSamples({ tenantId, onProgress } = {}) {
  const first = await fetchSamplesPage(1, tenantId);
  const samples = [...first.samples];
  const total = Number.isFinite(first.totalRecords) ? first.totalRecords : null;
  if (onProgress) onProgress({ loaded: samples.length, total });

  if (total !== null) {
    const pageCount = Math.ceil(total / PAGE_SIZE);
    const pages = [];
    for (let page = 2; page <= pageCount; page++) pages.push(page);
    const results = new Array(pages.length);
    let cursor = 0;
    const worker = async () => {
      while (cursor < pages.length) {
        const i = cursor++;
        results[i] = await fetchSamplesPage(pages[i], tenantId);
        if (onProgress) {
          const loaded = samples.length + results.reduce((n, r) => n + (r ? r.samples.length : 0), 0);
          onProgress({ loaded, total });
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(PAGE_CONCURRENCY, pages.length) }, worker));
    for (const result of results) samples.push(...result.samples);
    return samples;
  }

  let page = 1;
  let last = first;
  while (last.samples.length === PAGE_SIZE) {
    page += 1;
    last = await fetchSamplesPage(page, tenantId);
    samples.push(...last.samples);
    if (onProgress) onProgress({ loaded: samples.length, total: null });
  }
  return samples;
}

export async function fetchMetadataTables(tenantId) {
  try {
    return await fetchMetaData(tenantId);
  } catch (error) {
    const cached = await getCachedMetaData();
    if (cached) return cached;
    throw error;
  }
}
